import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import { toApiInstant } from '@/lib/store-time';
import type { BookingLocation } from '@/features/memberships/types';
import type { OverviewDashboard, PagedOverviewRows } from './types';

/**
 * `AdminOverviewSearchModel`.
 *
 * "No location" is its own flag rather than a magic id: a plan with no location
 * has no id to send, and overloading one would make the two states
 * indistinguishable.
 */
export interface OverviewSearchModel {
  startDate: string;
  endDate: string;
  locationId: string | null;
  noLocation: boolean;
}

/**
 * The window the API is asked about, in its terms.
 *
 * Built once and shared with the drill-down, never restated there: a breakdown
 * that asked about a slightly different range would return rows that do not add
 * up to the tile which opened it, and the reader would have no way to tell
 * which of the two was wrong.
 *
 * The API filters in UTC and the picker gives local dates, so shift by the
 * configured offset the way every other v2 page does.
 */
export function toApiRange(start: string, end: string) {
  const shift = (value: string, addDays = 0) => {
    // Built in UTC fields so the browser's own zone never enters the sum.
    const date = new Date(`${value}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + addDays);
    return toApiInstant(date);
  };
  // The end is exclusive on the API, so the whole of the last day is included
  // by asking for the start of the next one.
  return { startDate: shift(start), endDate: shift(end, 1) };
}

export function useOverviewDashboard(model: OverviewSearchModel) {
  return useQuery({
    queryKey: ['overview', 'dashboard', model],
    queryFn: () =>
      unwrap<OverviewDashboard>(
        bookingApi.post('v2/admin/overview/dashboard', model)
      ),
    placeholderData: keepPreviousData,
  });
}

/** The rows behind one number on the money or activity tab. */
export function useOverviewRows({
  model,
  metric,
  source,
  page,
  size,
}: {
  model: OverviewSearchModel;
  metric: number | null;
  /**
   * Only used by the revenue-by-source tiles, each of which is Collected
   * confined to one source. Passing it rather than giving those three their own
   * metrics means they are answered from the same query as the headline
   * Collected figure, so the three still add up to it.
   */
  source: number | null;
  page: number;
  size: number;
}) {
  return useQuery({
    queryKey: ['overview', 'rows', model, metric, source, page, size],
    queryFn: () =>
      unwrap<PagedOverviewRows>(
        bookingApi.post('v2/admin/overview/rows', {
          ...model,
          metric,
          source,
          page,
          size,
        })
      ),
    enabled: metric !== null,
    placeholderData: keepPreviousData,
  });
}

export function useBookingLocations() {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () =>
      unwrap<BookingLocation[]>(
        bookingApi.get<ApiEnvelope<BookingLocation[]>>('locations')
      ),
    staleTime: 5 * 60 * 1000,
  });
}
